import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useBoolean } from 'react-use'
import base64 from 'base-64'
import clsx from 'clsx'
import { v4 as uuidV4 } from 'uuid'

import { DBPluginAPI, OrgPluginsAPI, PluginsAPI, TableColummnsAPI, TableDataAPI, TablesAPI } from '@makyoapp/makyo-sdk'
import { Order } from '@makyoapp/makyo-sdk/api/api'
import { DatabasesAPI, ITableExternalDBBody } from '@makyoapp/makyo-sdk/api/Databases'
import { RequirementValue } from '@makyoapp/makyo-sdk/api/Organizations'
import { IDatabase, IDatabasePresenter } from '@makyoapp/makyo-sdk/models/Database'
import { PluginOrderBy } from '@makyoapp/makyo-sdk/models/Plugins'
import { ITableColumn, ITableRow } from '@makyoapp/makyo-sdk/models/Table'
import { ITeamBrief } from '@makyoapp/makyo-sdk/models/Teams'
import { DEFAULT_COL_OPTIONS, OrgCardAdapter, TeamAdapter } from '@makyoapp/makyo-common'
import { Excel, Makyo } from '@makyoapp/makyo-icon/colored'
import { Check16, Close8, Plus16 } from '@makyoapp/makyo-icon/icon'
import { Spreadsheet } from '@makyoapp/makyo-icon/mimetype'
import {
  AvatarCell,
  Button,
  ButtonColor,
  ButtonVariant,
  Checkbox,
  Field,
  JustifyVariant,
  Label,
  Modal,
  SimpleInput,
  Table,
  Td,
  TextSwitch,
  Th,
  Tr,
} from '@makyoapp/makyo-ui'

import { toasted } from '@/lib/toast'
import { appConfig } from '@/configs'
import { fromSdkDb } from '@/adapters/DbAdapter'
import { useGetOrgTeams } from '@/resolvers/org/query'
import { useGetMyTrashList } from '@/resolvers/user/query'
import { DbPluginListItem, DbTestFlag, DbTestType, useCreateDbContext } from '@/context/CreateDbContext'
import { useCurrentOrgContext } from '@/context/CurrentOrgContext'
import { useDbContext } from '@/context/DbContext'
import useAuth from '@/hooks/useAuth'
import { useReqError } from '@/hooks/useReqError'
import { CREATE_COL_DEFAULT } from '@/shared/ag-grid-constants'
import { ORG_DATABASE_ROUTE, ORG_ROUTE } from '@/shared/constants'
import { cleanUpString, debounce } from '@/shared/utils'
import { DbSourceCardSmall } from '@/components/DbSourceCard'
import { Info } from '@/components/Info'
import { SaveButton } from '@/components/SaveButton'

import { CreateDbMakyoAI } from './components/CreateDbModal/CreateDbMakyoAI'

const dummyData: DbPluginListItem[] = [
  {
    id: 'makyo-db',
    name: 'MakyoDB',
    type: 'db',
    icon: <Makyo width={16} height={16} className='fill-primary-500' />,
  },
]
/**
 * creat db / edit db team permission modal props
 * team modal is reused in create db and edit db permission
 */
interface CreateDbSelectTeamProps {
  /**
   * the teams value
   */
  value: TeamAdapter[]
  /**
   * the update teams value callback
   * @param nextValue
   */
  setValue: (nextValue: TeamAdapter[]) => void
  /**
   * forced teams
   */
  forcedTeams: TeamAdapter[]
}

/**
 * create new db, select team modal
 * used by all source
 */
export function CreateDbSelectTeam(props: CreateDbSelectTeamProps) {
  const { data: orgTeams } = useGetOrgTeams()

  const { userData } = useAuth()
  const { value, setValue, forcedTeams } = props
  const teamOptions = orgTeams || []

  const filteredTeamsOptions = teamOptions.filter((dt) => {
    return dt.members.find((tm) => tm.userId === userData?.id) !== undefined
  })

  const { t } = useTranslation()
  return (
    <div>
      <div className={clsx('pt-4', filteredTeamsOptions.length > 0 ? 'pb-8' : 'pb-2')}>
        {filteredTeamsOptions.length > 0
          ? t('Please select team that are permitted to use this database in their apps')
          : t("You're not related to any teams")}
      </div>
      {filteredTeamsOptions.length > 0 ? (
        <Table className='border bdr'>
          <thead>
            <Tr>
              <Th className='chk'>
                <Checkbox
                  id='create-db-team-all'
                  checked={value.length === filteredTeamsOptions.length}
                  onClick={(isChecked) => {
                    setValue(isChecked ? [...filteredTeamsOptions] : [])
                  }}
                />
              </Th>
              <Th>{t('Team')}</Th>
            </Tr>
          </thead>
          <tbody>
            {filteredTeamsOptions.map((team) => {
              const disabled = forcedTeams.find((fc) => fc.id === team.id)
              return (
                <Tr key={team.id}>
                  <Td className='chk'>
                    <Checkbox
                      disabled={Boolean(disabled)}
                      id={`create-db-team-${team.id}`}
                      checked={value.filter((t) => t.id === team.id).length > 0}
                      onClick={(isChecked) => {
                        if (!Boolean(disabled)) {
                          setValue(isChecked ? [...value, team] : value.filter((t) => t.id !== team.id))
                        }
                      }}
                    />
                  </Td>
                  <Td>
                    <AvatarCell {...team} />
                  </Td>
                </Tr>
              )
            })}
          </tbody>
        </Table>
      ) : (
        <div className='text-light'>{t('You can add a new team, then assign the permission later.')}</div>
      )}
    </div>
  )
}

/**
 * For makyo db
 */
function CreateDbMakyoSource() {
  const { t } = useTranslation()
  const { allItems } = useDbContext()
  const { value, setValue, setError, error } = useCreateDbContext()
  const isMakyoAI = value.sourceAlt?.name === 'MakyoAI'

  const myTrashQuery = useGetMyTrashList()

  // debounce validate db name
  const debouncedValidateDbName = useCallback(
    debounce(async (name: string) => {
      // Check active databases
      const dbExists = allItems?.some((db) => db.name.toLowerCase() === name.toLowerCase())

      if (dbExists) {
        setError('Database exists')
        return
      }

      // Check trashed databases
      const trashedDb = (myTrashQuery.data || []).find(
        (item) => item._ItemType === 'database' && item._ItemName.toLowerCase() === name.toLowerCase()
      )
      if (trashedDb) {
        setError(t('This database name already exists in trash'))
        return
      }

      setError('')
      //
    }, 500),
    [allItems, t, myTrashQuery.data]
  )

  const handleChangeName = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const v = e.currentTarget.value
      setValue({ db: v })

      debouncedValidateDbName(v)
    },
    [setValue, debouncedValidateDbName]
  )

  return (
    <div>
      <Field>
        <Label defaultSize={false} required className='lbl'>
          {t('Database Name')}
        </Label>
        <div className='flex-1 relative'>
          <SimpleInput
            id='create-db-name'
            required
            value={value.db}
            error={error ? t(error) : undefined}
            onChange={handleChangeName}
            type='text'
            withCounterConfig={appConfig.charConstraints}
            disabled={myTrashQuery.isLoading}
          />
          {myTrashQuery.isLoading && <div className='loading spin absolute right-2 top-[25%] translate-y-[-25%] ' />}
        </div>
      </Field>
      {!isMakyoAI && (
        <Field isCenter>
          <Label defaultSize={false} className='lbl'>
            {t('Include Makyo sample data?')}
          </Label>
          <TextSwitch
            id='create-db-sample'
            justify={JustifyVariant.Start}
            checked={value.sampleTables}
            textOn={t('Yes')}
            textOff={t('No')}
            onClick={(e) => setValue({ sampleTables: e })}
          />
        </Field>
      )}
    </div>
  )
}

function dbToDbPresenter(resDb: IDatabase, currentOrg: OrgCardAdapter, teams: TeamAdapter[]): IDatabasePresenter {
  return {
    _DefaultLanguage: 'en',
    _Records: 1,
    _Size: 0,
    _Source: '',
    _id: resDb._id,
    _Name: resDb._Name,
    _Teams: teams.map((t) => {
      const now = new Date()
      return {
        _id: t.id,
        _Name: t.name,
        _Avatar: t.avatar || '',
        _Description: '',
        _AccessedAt: now,
        _CreatedAt: now,
        _UpdatedAt: now,
      } as ITeamBrief
    }),
    _IsExternalDB: resDb._IsExternalDB,
    _ExternalDB: resDb._ExternalDB,
    _AccessedAt: resDb._AccessedAt,
    _Tables: 1,
    _CreatedAt: resDb._CreatedAt,
    _UpdatedAt: resDb._UpdatedAt,
    _Languages: [],
    _Projects: [],
    _Organization: {
      _id: currentOrg.id,
      _Name: currentOrg.name,
      _UpdatedAt: currentOrg.updated,
      _CreatedAt: currentOrg.created,
      _AccessedAt: currentOrg.accessed,
      _Avatar: currentOrg.avatar || '',
      _Description: '',
    },
  }
}
/**
 * handle click hook callback
 * this function return a callback handler function
 * to use within onClick prop for button when creating db
 */
function useHandleClickCb({
  setIsLoading,
  onSubmit,
  onFinally,
}: {
  setIsLoading: (next: boolean) => void
  onSubmit?: (dbId: string) => void
  onFinally?: () => void
}) {
  const { value, reset, setShowModal, extTables, importType } = useCreateDbContext()
  const { currentOrg } = useCurrentOrgContext()
  const { setAllItems, allItems } = useDbContext()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const printAPIError = useReqError()

  return useCallback(() => {
    if (currentOrg) {
      const isMakyoDb = value.sourceAlt?.id === 'makyo-db'
      setIsLoading(true)

      const name = value.db

      const promise = !isMakyoDb
        ? Promise.all(
            extTables.map((extTable) => {
              return OrgPluginsAPI.ReqGetTableSchema(currentOrg.id, value.pluginRefId || '', {
                _Name: extTable,
              })
                .then((res) => {
                  return {
                    _Name: extTable,
                    _Columns: res.map((cs) => {
                      return {
                        _Name: cs.Name,
                        _Type: cs.Type,
                        _Settings: DEFAULT_COL_OPTIONS,
                      } as ITableColumn
                    }),
                  } as ITableExternalDBBody
                })
                .catch((e) => {
                  printAPIError(e)
                  return null
                })
            })
          ).then((tables) => {
            return DatabasesAPI.ReqAddExternalDatabase({
              _Name: name,
              _Teams: value.teams.map((tm) => tm.id),
              _IsExternalDB: true,
              _ExternalDB: {
                _OrgPluginID: value.pluginRefId ?? '',
                _Synced: false,
                _Syncing: false,
                _LastSyncAt: new Date(),
              },
              _SampleData: value.sampleTables,
              _OrganizationID: currentOrg.id,
              _Tables: tables as ITableExternalDBBody[],
            }).then((resDb) => {
              const dbPresenter = dbToDbPresenter(resDb, currentOrg, value.teams)
              return Promise.all([
                Promise.resolve() as unknown as Promise<void>,
                Promise.resolve() as unknown as Promise<void>,
                Promise.resolve(fromSdkDb(dbPresenter)),
              ])
            })
          })
        : DatabasesAPI.ReqAddDatabase({
            _Name: name,
            _Teams: value.teams.map((tm) => tm.id),
            _IsExternalDB: !isMakyoDb,
            _ExternalDB: {
              _OrgPluginID: value.pluginRefId ?? '',
              _Synced: false,
              _Syncing: false,
              _LastSyncAt: new Date(),
            },
            _SampleData: value.sampleTables,
            _OrganizationID: currentOrg.id,
          }).then((resDb) => {
            const dbPresenter = dbToDbPresenter(resDb, currentOrg, value.teams)
            const db = fromSdkDb(dbPresenter)

            return TablesAPI.ReqAdd('Table 1', resDb._id).then((tb) => {
              return Promise.all([
                TableColummnsAPI.ReqAdd(tb._id, CREATE_COL_DEFAULT) as unknown as Promise<void>,
                TableDataAPI.ReqAdd(tb._id, 'en', {}) as unknown as Promise<void>,
                Promise.resolve(db),
              ])
            })
            //}
          })

      promise
        .then(([, , db]) => {
          if (db) {
            setAllItems((prev) => {
              if (!!prev && Array.isArray(prev)) {
                // New database
                const updatedList = [db, ...prev]
                // Sort by timestamp created in descending
                updatedList.sort((a, b) => new Date(b.created).getTime() - new Date(a.created).getTime())
                return updatedList
              } else {
                return prev
              }
            })
            if (!isMakyoDb) {
              DatabasesAPI.ReqSync(db.id)
                .then(() => {
                  toasted(t('Database Syncing'))
                })
                .catch(printAPIError)
            }

            toasted(t('Database created!'))

            if (!onSubmit) {
              if (currentOrg) {
                let nextRoute = `${ORG_ROUTE}/${currentOrg.id}/${ORG_DATABASE_ROUTE}/${db.id}`
                navigate(nextRoute, {
                  state: {
                    importType,
                  },
                })
              }
            } else {
              onSubmit(db.id)
            }
          }
        })
        .catch((e) => {
          printAPIError(e)
        })
        .finally(() => {
          reset()
          onFinally?.()
          setIsLoading(false)
          setShowModal(false)
        })
    }
  }, [
    currentOrg,
    extTables,
    importType,
    navigate,
    onFinally,
    onSubmit,
    printAPIError,
    reset,
    setAllItems,
    setIsLoading,
    setShowModal,
    t,
    value.db,
    value.pluginRefId,
    value.sampleTables,
    value.sourceAlt?.id,
    value.teams,
  ])
}

interface CreateDbTestListProps {
  flag: DbTestFlag
  children?: React.ReactNode
}

function CreateDbTestList({ flag, children }: CreateDbTestListProps) {
  let icon

  switch (flag) {
    case DbTestFlag.Loading:
      icon = <span className='loading spin' />
      break
    case DbTestFlag.Success:
      icon = <Check16 className='fill-primary-500' />
      break
    case DbTestFlag.Failed:
      icon = <Close8 className='red' />
      break
    default:
      icon = <div>?</div>
  }

  return (
    <li className='flex items-center gap-2'>
      {icon && <div className='w-6 h-6 flex-cc'>{icon}</div>}
      {children}
    </li>
  )
}

/**
 * Step 2: Settings
 */
function CreateDbItemSettings({
  onBack,
  onSubmit,
  onFinally,
}: {
  onBack?: (step: number) => void
  onSubmit?: (dbId: string) => void
  onFinally?: () => void
}) {
  const {
    value,
    setValue,
    setStep,
    step,
    testState,
    testDispatch,
    error,
    requirements,
    setRequirements,
    extTables,
    setExtTables,
    forcedTeams,
  } = useCreateDbContext()

  const isMakyoAI = value.sourceAlt?.name === 'MakyoAI'
  const isMakyoDB = value.sourceAlt?.id === 'makyo-db'
  const isDb = value.sourceAlt?.type === 'db'
  const backend = value.sourceAlt?.data?._Backend
  const fe = value.sourceAlt?.data?._Frontend

  const { userData } = useAuth()
  const { t } = useTranslation()

  const { currentOrg } = useCurrentOrgContext()
  const [isLoading, setIsLoading] = useBoolean(false)
  const handleClick = useHandleClickCb({ setIsLoading, onSubmit, onFinally })

  let el, footer

  const script = null
  let extra = null

  const [showConnection, setShowConnection] = useBoolean(false)
  const divRef = useRef<HTMLDivElement>(null)
  const cb = useCallback(
    (tagName: string) => (
      <Field>
        <div
          ref={divRef}
          dangerouslySetInnerHTML={{
            __html: `<${tagName} requirements="${
              requirements ? base64.encode(JSON.stringify(requirements)) : ''
            }" locale="en" redirect="${window.location.protocol}//${
              window.location.host
            }/plugins/oauthdb/auth" state="${base64.encode(
              JSON.stringify({
                PluginID: value.sourceAlt?.id,
                Ref: uuidV4(),
              })
            )}">Loading</${tagName}>`,
          }}
        ></div>
      </Field>
    ),
    [requirements, value, divRef]
  )

  useEffect(() => {
    const el = divRef.current?.children?.[0]
    const cb = async (e: Event) => {
      const evt = e as unknown as CustomEvent
      if (requirements) {
        const { metadata, ...others } = evt.detail ?? { metadata: { name: '' } }
        //const others = evt.detail ?? { metadata: { name: '' } }
        setRequirements({
          ...requirements,
          ...others,
        })
        if (others?.metadata?.name && currentOrg && value.sourceAlt) {
          setValue({
            db: others.metadata.name.toString(),
          })
        }
        setShowConnection(true)
      }
    }

    el?.addEventListener('on-requirements-ready', cb)

    return () => {
      el?.removeEventListener('on-requirements-ready', cb)
    }
  }, [divRef, requirements, setRequirements, setShowConnection, setValue, currentOrg, value.sourceAlt])

  useEffect(() => {
    let timeout: NodeJS.Timeout

    if (value && !!value.db) {
      timeout = setTimeout(() => {
        setValue({
          ...value,
          db: cleanUpString(value.db),
        })
      }, 1000)
    }

    return () => {
      clearTimeout(timeout)
    }
  }, [value, setValue])

  if (fe?._HtmlTagName) {
    extra = cb(fe._HtmlTagName)
  }

  const [tables, setTables] = useState<string[]>([])

  if (Array.isArray(backend)) {
    if (step === 2) {
      el = (
        <>
          {script}
          {extra}
          <div>
            {backend.map((b, idx: number) => {
              return b._Type !== 'user' ? null : (
                <Field key={idx}>
                  <Label>{b._Label[userData?.lang || 'en']}</Label>
                  <div className='w-full'>
                    <SimpleInput
                      value={value.dbSettings?.[b._Key] || ''}
                      // @ts-ignore
                      id={`${b._id}-${b._Key}`}
                      onChange={(e) => {
                        setValue({
                          dbSettings: {
                            ...value.dbSettings,
                            [b._Key]: e.currentTarget.value,
                          },
                        })
                      }}
                    />
                    <div className='help mt-1 text-m-sm text-light'>{b._Description[userData?.lang || 'en']}</div>
                  </div>
                </Field>
              )
            })}
            {(isDb || showConnection) && (
              <Field>
                {!showConnection && <Label></Label>}
                <div className='w-full'>
                  <Button
                    disabled={backend.some((bk) => {
                      return bk._IsMandatory ? !Boolean(value.dbSettings[bk._Key]) : false
                    })}
                    variant={ButtonVariant.Outlined}
                    onClick={async () => {
                      const tests: [
                        (
                          | ((pluginID: string, requirements: RequirementValue) => Promise<ITableRow | null>)
                          | ((
                              pluginID: string,
                              requirements: RequirementValue,
                              rowId: string
                            ) => Promise<ITableRow | null>)
                        ),
                        DbTestType,
                      ][] = [
                        [DBPluginAPI.ReqTestPing, DbTestType.Ping],
                        [DBPluginAPI.ReqTestCreateTable, DbTestType.CreateTable],
                        [DBPluginAPI.ReqTestAddRow, DbTestType.AddRow],
                        [DBPluginAPI.ReqTestGetRow, DbTestType.GetRow],
                        [DBPluginAPI.ReqTestUpdateRow, DbTestType.UpdateRow],
                        [DBPluginAPI.ReqTestDeleteRow, DbTestType.DeleteRow],
                        [DBPluginAPI.ReqTestDeleteTable, DbTestType.DeleteTable],
                      ]
                      let returnedRow: null | ITableRow = null

                      for (const [testFn, testType] of tests) {
                        if (value.sourceAlt?.id) {
                          try {
                            testDispatch({
                              type: testType,
                              payload: DbTestFlag.Loading,
                            })
                            returnedRow = await testFn(
                              value.sourceAlt.id,
                              {
                                ...value.dbSettings,
                                ...requirements,
                              } as Record<string, string>,
                              returnedRow?.['_id'] ?? ''
                            )
                            testDispatch({
                              type: testType,
                              payload: DbTestFlag.Success,
                            })
                          } catch (e: unknown) {
                            testDispatch({
                              type: testType,
                              payload: DbTestFlag.Failed,
                              error: `${(e as { message: string }).message}`,
                            })
                            break
                          }
                        }
                      }
                    }}
                  >
                    <Trans>Test Connection</Trans>
                  </Button>
                  <ul className='pt-2 flex flex-col gap-2'>
                    {testState.ping !== 0 && (
                      <CreateDbTestList flag={testState.ping}>
                        <Trans>Testing connection to database</Trans>
                      </CreateDbTestList>
                    )}
                    {testState.addRow !== 0 && (
                      <CreateDbTestList flag={testState.createTable}>
                        <Trans>Creating test table</Trans>
                      </CreateDbTestList>
                    )}
                    {testState.addRow !== 0 && (
                      <CreateDbTestList flag={testState.addRow}>
                        <Trans>Adding a row</Trans>
                      </CreateDbTestList>
                    )}
                    {testState.getRow !== 0 && (
                      <CreateDbTestList flag={testState.getRow}>
                        <Trans>Getting a row</Trans>
                      </CreateDbTestList>
                    )}
                    {testState.updateRow !== 0 && (
                      <CreateDbTestList flag={testState.updateRow}>
                        <Trans>Updating a row</Trans>
                      </CreateDbTestList>
                    )}
                    {testState.delRow !== 0 && (
                      <CreateDbTestList flag={testState.delRow}>
                        <Trans>Deleting a row</Trans>
                      </CreateDbTestList>
                    )}
                    {testState.delTable !== 0 && (
                      <CreateDbTestList flag={testState.delTable}>
                        <Trans>Deleting test table</Trans>
                      </CreateDbTestList>
                    )}
                  </ul>
                  {testState.error && (
                    <Info isDanger className='mt-4 bg-red-100 p-4 w-full'>
                      {testState.error}
                    </Info>
                  )}
                </div>
              </Field>
            )}
          </div>
        </>
      )
    } else if (step === 3) {
      el = (
        <div>
          <div className='text-base pt-4 pb-8'>
            <Trans>Select table</Trans>
          </div>
          <div className='h-[300px] overflow-y-auto'>
            <Table className='border bdr'>
              <thead>
                <Tr>
                  <Th className='chk'>
                    <Checkbox
                      id='create-db-select-table'
                      checked={tables.length === extTables.length && extTables.length > 0}
                      onClick={(isChecked) => {
                        setExtTables(isChecked ? [...tables] : [])
                      }}
                    />
                  </Th>
                  <Th>
                    <Trans>Table</Trans>
                  </Th>
                </Tr>
              </thead>
              <tbody>
                {tables.map((table) => {
                  return (
                    <Tr key={table}>
                      <Td className='chk'>
                        <Checkbox
                          id={`create-db-table-${table}`}
                          checked={extTables.filter((t) => t === table).length > 0}
                          onClick={(isChecked) => {
                            setExtTables(isChecked ? [...extTables, table] : extTables.filter((t) => t !== table))
                          }}
                        />
                      </Td>
                      <Td>{table}</Td>
                    </Tr>
                  )
                })}
              </tbody>
            </Table>
          </div>
        </div>
      )
    } else {
      el = (
        <CreateDbSelectTeam
          forcedTeams={forcedTeams}
          value={value.teams}
          setValue={(teams) => {
            setValue({
              teams,
            })
          }}
        />
      )
    }

    footer = (
      <Modal.Footer className='justify-between'>
        <CreateDbModalBack onBack={onBack} />
        <div className='flex-c'>
          <SaveButton
            colorStyle={ButtonColor.Tertiary}
            isLoading={isLoading}
            disabled={
              backend.some((bk) => {
                return bk._IsMandatory && bk._Type !== 'implicit' ? !Boolean(value.dbSettings[bk._Key]) : false
              }) ||
              (isDb && !testState.ended) ||
              (isDb && testState.ended && Boolean(testState.error)) ||
              (!isDb && !showConnection) ||
              (!isDb && showConnection && !testState.ended) ||
              (testState.ended && Boolean(testState.error))
            }
            onClick={() => {
              if (step <= 3) {
                if (step === 2 && value.sourceAlt?.id !== 'makyo-db' && currentOrg) {
                  /*const name = genIdent(
                    (allItems ?? []) as unknown as Record<string, string>[],
                    'name',
                    1,
                    value.db
                  )*/
                  const name = value.db
                  setIsLoading(true)
                  OrgPluginsAPI.ReqAdd(currentOrg.id, {
                    _Label: `${value.sourceAlt?.name} used for database ${name}`,
                    _PluginID: value.sourceAlt?.id ?? '',
                    _Backend: {
                      ...((requirements ?? {}) as RequirementValue),
                    },
                    _Frontend: {},
                  })
                    .then((res) => {
                      setValue({
                        pluginRefId: res._id,
                      })
                      return OrgPluginsAPI.ReqListTables(currentOrg.id, res._id)
                    })
                    .then((res) => {
                      setTables(res)
                    })
                    .finally(() => {
                      setIsLoading(false)
                      setStep(step + 1)
                    })
                } else {
                  setStep(step + 1)
                }
              } else {
                handleClick()
              }
            }}
            label={t('Next')}
          />
        </div>
      </Modal.Footer>
    )
  }

  const dbNameCharCount = value.db.trim().length
  const validDbNameLength =
    dbNameCharCount >= appConfig.charConstraints.min && dbNameCharCount < appConfig.charConstraints.max

  if (isMakyoDB || isMakyoAI) {
    if (step < 3) {
      el = <CreateDbMakyoSource />
    } else {
      el = (
        <CreateDbSelectTeam
          forcedTeams={forcedTeams}
          value={value.teams}
          setValue={(teams) => {
            setValue({
              teams,
            })
          }}
        />
      )
    }

    footer = (
      <Modal.Footer className='justify-between'>
        <CreateDbModalBack onBack={onBack} />
        <SaveButton
          colorStyle={ButtonColor.Tertiary}
          isLoading={isLoading}
          disabled={!validDbNameLength || Boolean(error) || (step > 2 && value.teams.length < 1)}
          onClick={() => {
            if (isMakyoAI) {
              setStep(step + 1)
            } else if (isMakyoDB && step === 2) {
              setStep(step + 1)
            } else {
              handleClick()
            }
          }}
          label={t('Next')}
        />
      </Modal.Footer>
    )
  }

  if (value.sourceAlt?.name === 'MakyoAI' && step === 4) {
    el = <CreateDbMakyoAI type={value.aiType as any} onCreateApp={onSubmit} onEnd={onFinally} />

    footer = null
  }

  return (
    <>
      <CreateDbModalBody>{el}</CreateDbModalBody>
      {footer}
    </>
  )
}

function CreateDbModalBody({ children }: { children: React.ReactNode }) {
  return (
    <Modal.Body>
      <div className='modal-md min-h-[356px] flex flex-col'>{children}</div>
    </Modal.Body>
  )
}

function CreateDbModalBack({ onBack }: { onBack?: (step: number) => void }) {
  const { setStep, step, value, reset, backButtonText } = useCreateDbContext()
  return (
    <Button
      colorStyle={ButtonColor.Tertiary}
      variant={ButtonVariant.Outlined}
      onClick={() => {
        setStep(step - 1)
        onBack?.(step)
        if (step === 1) {
          reset()
        }
      }}
    >
      <Trans>{backButtonText}</Trans>
    </Button>
  )
}

/**
 * Step 1: Select Engine
 */
function CreateDbSelectEngine({
  dbPlugins,
  isLoading,
  onNext,
  onBack,
}: {
  isLoading: boolean
  dbPlugins: DbPluginListItem[]
  onNext?: () => void
  onBack?: (step: number) => void
}) {
  const { setStep, setValue, setImportType } = useCreateDbContext()
  const { t } = useTranslation()

  const allDbOptions = [dummyData[0], ...dbPlugins]

  // Get makyo AI plugins
  const makyoAIplugin = dbPlugins.find((p) => p.name === 'MakyoAI')

  // Group by type
  const allDbOptionsMapByType = allDbOptions.reduce(
    (acc, curr) => {
      if (acc[curr.type]) {
        acc[curr.type].push(curr)
      } else {
        acc[curr.type] = [curr]
      }
      return acc
    },
    {} as Record<string, DbPluginListItem[]>
  )

  const handleSetSourceAlt = useCallback(
    (dm: DbPluginListItem, aiType?: string) => {
      setStep(2)
      setValue({
        sourceAlt: dm,
        aiType,
      })
    },
    [setStep, setValue]
  )

  return (
    <>
      <CreateDbModalBody>
        {isLoading ? (
          <div className='flex-cc min-h-[356px]'>
            <span className='loading spin' />
          </div>
        ) : (
          <div className='space-y-6'>
            <div className='space-y-2'>
              <label className='text-sm font-semibold'>{t('Database')}</label>
              <div className='mt-1 grid grid-cols-1 md:grid-cols-2 gap-4 min-w-[320px]'>
                {allDbOptionsMapByType.db &&
                  allDbOptionsMapByType.db.map((dm) => {
                    return (
                      <DbSourceCardSmall
                        key={dm.id}
                        title={dm.name}
                        icon={dm.icon}
                        onClick={() => {
                          handleSetSourceAlt(dm)
                          onNext?.()
                        }}
                      />
                    )
                  })}
              </div>
            </div>

            {/*
            <label className='text-sm font-semibold'>{t('3rd Party Data')}</label>
            <div className='grid grid-cols-1 md:grid-cols-2 gap-4 min-w-[320px] my-3'>
              {allDbOptionsMapByType.api &&
                allDbOptionsMapByType.api.map((dm) => {
                  return dm.name !== 'Stripe' ? (
                    <DbSourceCardSmall
                      key={dm.id}
                      title={dm.name}
                      icon={dm.icon}
                      onClick={() => {
                        handleSetSourceAlt(dm)
                        onNext?.()
                      }}
                    />
                  ) : null
                })}
            </div> */}

            {makyoAIplugin && (
              <div className='space-y-2'>
                {/* <label className='text-sm font-semibold'>{t('Content to Database ✨')}</label> */}
                <label className='text-sm font-semibold'>{t('Makyo AI ✨')}</label>
                <div className='mt-1 grid grid-cols-1 md:grid-cols-2 gap-4 min-w-[320px]'>
                  <DbSourceCardSmall
                    key={makyoAIplugin.id}
                    // title={makyoAIplugin.name}
                    title='Content to Database'
                    icon={makyoAIplugin.icon}
                    isAI={true}
                    onClick={() => {
                      handleSetSourceAlt(makyoAIplugin, 'content-to-database')
                      onNext?.()
                    }}
                  />
                  <DbSourceCardSmall
                    key={'makyo-ai-json'}
                    // title={makyoAIplugin.name}
                    title='Prompt to JSON'
                    icon={makyoAIplugin.icon}
                    isAI={true}
                    onClick={() => {
                      handleSetSourceAlt(makyoAIplugin, 'prompt-to-json')
                      onNext?.()
                    }}
                  />
                </div>
              </div>
            )}

            <div className='space-y-2'>
              <label className='text-sm font-semibold'>{t('Import')}</label>
              <div className='mt-1 grid grid-cols-1 md:grid-cols-2 gap-4 min-w-[320px]'>
                <DbSourceCardSmall
                  key={'excel-import'}
                  title={'Microsoft Excel'}
                  icon={<Excel width={16} height={16} />}
                  onClick={() => {
                    setImportType('excel')
                    handleSetSourceAlt(dummyData[0])
                    onNext?.()
                  }}
                />
                <DbSourceCardSmall
                  key={'csv-import'}
                  title={'CSV File'}
                  icon={<Spreadsheet width={16} height={16} />}
                  onClick={() => {
                    setImportType('csv')
                    handleSetSourceAlt(dummyData[0])
                    onNext?.()
                  }}
                />
              </div>
            </div>
          </div>
        )}
      </CreateDbModalBody>
      <Modal.Footer className='justify-between'>
        <CreateDbModalBack onBack={onBack} />
      </Modal.Footer>
    </>
  )
}

export function CreateDbModal({
  onSubmit,
  onFinally,
  noWrap = false,
  onBack,
  onNext,
}: {
  onSubmit?: (dbId: string) => void
  onFinally?: () => void
  onNext?: () => void
  onBack?: (step: number) => void
  noWrap?: boolean
}) {
  const { t } = useTranslation()
  const {
    reset,
    step,
    showModal,
    setShowModal,
    value,
    isLoading: isLoadingContext,
    backButtonText,
  } = useCreateDbContext()
  const printAPIError = useReqError()

  const isMakyoAI = value.sourceAlt?.name === 'MakyoAI'

  const onHide = () => {
    reset()
    setShowModal(false)
  }

  const [dbPlugins, setDbPlugins] = useState<DbPluginListItem[]>([])
  const [isLoading, setIsLoading] = useBoolean(false)

  useEffect(() => {
    if (dbPlugins.length < 1) {
      setIsLoading(true)
      PluginsAPI.ReqList(1, '', Order.Desc, PluginOrderBy.Name)
        .then((res) => {
          setDbPlugins(
            res._Data.map((dt) => {
              return {
                id: dt._id,
                name: dt._Name,
                icon: dt._Icon,
                data: dt,
                type: dt._Category === 'Database' ? 'db' : 'api',
              }
            })
          )
        })
        .catch(printAPIError)
        .finally(() => setIsLoading(false))
    }
  }, [setDbPlugins, setIsLoading, dbPlugins.length, printAPIError])

  let currentContent

  switch (step) {
    case 1:
      currentContent = (
        <CreateDbSelectEngine
          isLoading={isLoading}
          dbPlugins={dbPlugins}
          onNext={onNext}
          onBack={backButtonText === 'Cancel' ? onHide : onBack}
        />
      )
      break
    default:
      currentContent = <CreateDbItemSettings onBack={onBack} onSubmit={onSubmit} onFinally={onFinally} />
      break
  }

  const getTitle = useMemo(() => {
    if (isMakyoAI && step === 4) {
      if (value.aiType === 'content-to-database') {
        return t('Content to Database ✨')
      }
      if (value.aiType === 'prompt-to-json') {
        return t('Prompt to JSON ✨')
      }
    }
    return t('Add Database')
  }, [isMakyoAI, step, t, value.aiType])

  return (
    <>
      {!noWrap && (
        <Button id='create-db-btn' onClick={() => setShowModal(true)} icon={<Plus16 />}>
          {t('Add Database')}
        </Button>
      )}
      {!noWrap ? (
        <Modal
          id='create-db-modal'
          show={showModal}
          onHide={() => {
            if (isLoadingContext) return
            onHide()
          }}
          closeOnClickOutside={false}
        >
          <Modal.Header onCloseClick={onHide}>{getTitle}</Modal.Header>
          {currentContent}
        </Modal>
      ) : (
        currentContent
      )}
    </>
  )
}
